import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Bell, CalendarDays, Check, Clock, Film, Heart, Home, ListPlus, LogOut,
  Menu, Search, Settings, Share2, Star, UserRound, X
} from "lucide-react";
import logoURL from "../../IconSources/RolloIconPreview.svg";
import { api } from "./api.js";
import { shareReviewImage } from "./reviewShare.js";
import { formatDate, initials, joinClassNames, profilePath } from "./utils.js";

export function Brand({ compact = false }) {
  return (
    <Link className={joinClassNames("brand", compact && "brand--compact")} to="/">
      <img src={logoURL} alt="" />
      {compact ? null : <span>Rollo</span>}
    </Link>
  );
}

export function Surface({ as: Element = "div", className = "", children, ...props }) {
  return <Element className={`surface ${className}`} {...props}>{children}</Element>;
}

export function Avatar({ user, size = 44 }) {
  const style = { width: size, height: size, minWidth: size };
  return user?.avatarURL
    ? <img className="avatar" style={style} src={user.avatarURL} alt={`Foto de ${user.displayName}`} />
    : <span className="avatar avatar--fallback" style={style}>{initials(user)}</span>;
}

export function Poster({ movie, className = "" }) {
  return movie?.posterURL
    ? <img className={`poster ${className}`} src={movie.posterURL} alt={`Póster de ${movie.title}`} loading="lazy" />
    : <div className={`poster poster--empty ${className}`}><Film aria-hidden="true" /></div>;
}

export function RatingStars({ value = 0, onChange, size = 20, label = true }) {
  const interactive = typeof onChange === "function";
  const trackRef = useRef(null);
  const update = (clientX) => {
    if (!interactive || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 5;
    onChange(Math.min(5, Math.max(0.5, Math.ceil(raw * 2) / 2)));
  };
  return (
    <div
      ref={trackRef}
      className={joinClassNames("rating-stars", interactive && "rating-stars--interactive")}
      aria-label={label ? `${value} de 5 estrellas` : undefined}
      onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); update(event.clientX); }}
      onPointerMove={(event) => { if (event.buttons) update(event.clientX); }}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const fill = Math.min(1, Math.max(0, value - star + 1));
        return (
          <span className="star-wrap" key={star} style={{ width: size, height: size }}>
            <Star size={size} className="star-outline" />
            <span className="star-fill" style={{ width: `${fill * 100}%` }}><Star size={size} fill="currentColor" /></span>
          </span>
        );
      })}
    </div>
  );
}

export function Segmented({ options, value, onChange, ariaLabel }) {
  return (
    <div className="segmented" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          className={value === option.value ? "is-selected" : ""}
          key={option.value}
          onClick={() => onChange(option.value)}
          role="tab"
          aria-selected={value === option.value}
        >
          {option.icon || null}<span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Modal({ title, children, onClose, wide = false, fullMobile = false }) {
  useEffect(() => {
    const close = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={joinClassNames("modal surface", wide && "modal--wide", fullMobile && "modal--mobile-full")} role="dialog" aria-modal="true" aria-label={title}>
        <header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Cerrar"><X /></button></header>
        <div className="modal-body">{children}</div>
      </section>
    </div>,
    document.body
  );
}

export function Loading({ label = "Cargando…" }) {
  return <div className="loading"><span className="spinner" /><span>{label}</span></div>;
}

export function EmptyState({ icon: Icon = Film, title, text, action }) {
  return <div className="empty-state"><Icon /><h3>{title}</h3>{text ? <p>{text}</p> : null}{action || null}</div>;
}

export function Toast({ message, onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 3200);
    return () => clearTimeout(timer);
  }, [onDone]);
  return <div className="toast">{message}</div>;
}

export function WatchlistButton({ movie, initial, onChange }) {
  const [active, setActive] = useState(Boolean(initial ?? movie?.isInWatchlist));
  const [working, setWorking] = useState(false);
  const toggle = async () => {
    if (working) return;
    setWorking(true);
    try {
      const next = !active;
      await api(`/v1/me/watchlist/${movie.id}`, { method: next ? "POST" : "DELETE" });
      setActive(next);
      onChange?.(next);
    } finally { setWorking(false); }
  };
  return (
    <button className={joinClassNames("icon-button", "watchlist-button", active && "is-active")} onClick={toggle} disabled={working} aria-label={active ? "Quitar de Watchlist" : "Añadir a Watchlist"}>
      {active ? <Check /> : <Clock />}
    </button>
  );
}

function ReviewDetailModal({ review, onClose }) {
  return (
    <Modal title="Reseña" onClose={onClose} wide fullMobile>
      <article className="review-detail">
        <div
          className="review-detail-hero"
          style={review.movie.backdropURL || review.movie.posterURL ? { backgroundImage: `url(${review.movie.backdropURL || review.movie.posterURL})` } : undefined}
        >
          <Poster movie={review.movie} />
          <div><span>Una película en Rollo</span><h2>{review.movie.title}</h2><p>{review.movie.year || ""}</p><RatingStars value={review.rating} size={24} /></div>
        </div>
        <div className="review-detail-author"><Avatar user={review.user} size={46} /><div><strong>{review.user.displayName}</strong><span>@{review.user.username} · {formatDate(review.createdAt)}</span></div></div>
        {review.containsSpoilers ? <span className="spoiler-label">Esta reseña contiene spoilers</span> : null}
        <p className="review-detail-text">{review.text || "Sin texto."}</p>
      </article>
    </Modal>
  );
}

export function ReviewCard({ review, onChanged, showUser = true }) {
  const [liked, setLiked] = useState(Boolean(review.isLiked));
  const [likeCount, setLikeCount] = useState(Number(review.likeCount || 0));
  const [working, setWorking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 220 || review.text.split("\n").length > 4;
  const toggleLike = async () => {
    if (working) return;
    setWorking(true);
    try {
      const response = await api(`/v1/reviews/${encodeURIComponent(review.id)}/like`, { method: liked ? "DELETE" : "POST" });
      setLiked(response.isLiked);
      setLikeCount(response.likeCount);
      onChanged?.();
    } finally { setWorking(false); }
  };
  const share = async () => {
    await shareReviewImage(review);
  };
  return (
    <>
    <Surface as="article" className={joinClassNames("review-card", isLong && "review-card--expandable")}>
      {showUser ? (
        <div className="review-author">
          <Link to={profilePath(review.user.username)}><Avatar user={review.user} size={42} /></Link>
          <div><Link to={profilePath(review.user.username)}>{review.user.displayName}</Link><span>@{review.user.username}</span></div>
          <time>{formatDate(review.createdAt)}</time>
        </div>
      ) : null}
      <div className="review-main">
        <Link to={`/pelicula/${review.movie.id}`}><Poster movie={review.movie} /></Link>
        <div className="review-copy">
          <Link className="review-title" to={`/pelicula/${review.movie.id}`}>{review.movie.title} <span>{review.movie.year || ""}</span></Link>
          <RatingStars value={review.rating} size={19} />
          {review.containsSpoilers ? <span className="spoiler-label">Contiene spoilers</span> : null}
          <p className={joinClassNames(isLong && "review-excerpt")} onClick={() => isLong && setExpanded(true)}>{review.text || "Sin texto."}</p>
          {isLong ? <button className="review-more-button" onClick={() => setExpanded(true)}>Ver más</button> : null}
          <div className="review-actions">
            <button className={liked ? "is-liked" : ""} onClick={toggleLike} disabled={working}><Heart fill={liked ? "currentColor" : "none"} />{likeCount}</button>
            <button onClick={share}><Share2 />Compartir</button>
          </div>
        </div>
      </div>
    </Surface>
    {expanded ? <ReviewDetailModal review={review} onClose={() => setExpanded(false)} /> : null}
    </>
  );
}

const NAV_ITEMS = [
  ["/", "Inicio", Home],
  ["/buscar", "Buscar", Search],
  ["/diario", "Diario", CalendarDays],
  ["/perfil", "Perfil", UserRound]
];

export function AppShell({ children, user, onLogout }) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="app-shell">
      <aside className={joinClassNames("sidebar", mobileMenu && "is-open")}>
        <Brand />
        <nav>
          {NAV_ITEMS.map(([path, label, Icon]) => <NavLink key={path} to={path} end={path === "/"} onClick={() => setMobileMenu(false)}><Icon /><span>{label}</span></NavLink>)}
        </nav>
        <div className="sidebar-spacer" />
        <button className="sidebar-profile" onClick={() => navigate("/perfil")}><Avatar user={user} size={40} /><span><strong>{user.displayName}</strong><small>@{user.username}</small></span></button>
        <NavLink to="/ajustes"><Settings /><span>Ajustes</span></NavLink>
        <button className="nav-logout" onClick={onLogout}><LogOut /><span>Salir</span></button>
      </aside>
      <header className="mobile-header"><Brand compact /><button className="icon-button" onClick={() => setMobileMenu(!mobileMenu)}><Menu /></button></header>
      <main className="app-content">{children}</main>
      <nav className="mobile-tabs">
        {NAV_ITEMS.map(([path, label, Icon]) => <NavLink key={path} to={path} end={path === "/"}><Icon /><span>{label}</span></NavLink>)}
      </nav>
      {mobileMenu ? <button className="menu-scrim" onClick={() => setMobileMenu(false)} aria-label="Cerrar menú" /> : null}
    </div>
  );
}

export const Icons = { Bell, CalendarDays, Check, Clock, Film, Heart, Home, ListPlus, Search, Settings, Share2, Star, UserRound };
